import { Inject, Injectable, InjectionToken } from '@angular/core';
import {
  CasePartyViewModel,
  CaseRequestViewModel,
  RequestParticipantRepresentationViewModel,
  ICaseRequestUpdateService,
  ICaseRequestBuilderService,
  FsxCaseRequestBuilderService,
  FsxCaseRequestUpdateService,
} from '@fsx/fsx-shared';
import { Observable, Subject, switchMap, tap } from 'rxjs';
import {
  FsxValidatePartiesOrchestrationService,
  IValidatePartiesOrchestrationService,
} from '../../filing-editor/services/orchestration/validate-parties-orchestration.service';
import {
  FsxValidationErrorsService,
  IValidationErrorsService,
} from '../../filing-editor/services/validation-errors.service';

export const FsxRemoveRepresentationOrchestrationService =
  new InjectionToken<IRemoveRepresentationOrchestrationService>(
    'FsxRemoveRepresentationOrchestrationService'
  );

export interface IRemoveRepresentationEventParams {
  filingId: string;
  caseRequest: CaseRequestViewModel;
  representationToRemove: RequestParticipantRepresentationViewModel;
  partyToRemoveFrom: CasePartyViewModel;
}

export interface IRemoveRepresentationOrchestrationService {
  /**
   * The orchestration steps needed to remove representation from a party on the case request.
   */
  removeRepresentationFromPartyInCaseRequest$: Observable<CaseRequestViewModel>;

  /**
   * A method to allow orchestration to be triggered from components.
   *
   * @param params The parameters needed to initiate this orchestration.
   */
  removeRepresentation(params: IRemoveRepresentationEventParams): void;
}

@Injectable()
export class RemoveRepresentationOrchestrationService
  implements IRemoveRepresentationOrchestrationService
{
  /**
   * A subject to use as the trigger for the orchestration.
   */
  private removeRepresentation$$ =
    new Subject<IRemoveRepresentationEventParams>();

  /**
   * The orchestration steps needed to remove representation from a party on the case request.
   */
  removeRepresentationFromPartyInCaseRequest$: Observable<CaseRequestViewModel> =
    this.removeRepresentation$$.pipe(
      switchMap((params: IRemoveRepresentationEventParams) => {
        const caseRequestBackup = {
          ...params.caseRequest,
        } as CaseRequestViewModel;
        params.partyToRemoveFrom.representation =
          params.partyToRemoveFrom.representation?.filter((r) => {
            return (
              r.participantName !==
              params.representationToRemove.participantName
            );
          });

        // Attempt to remove the participant from the caseRequest.
        return this.caseRequestBuilderService
          .checkRemoveParticipant(
            params.caseRequest,
            params.representationToRemove.participantName
          )
          .pipe(
            switchMap(() => {
              // Remove any related validation errors for the representation being removed.
              this.validationErrorsService.removeEntityValidationErrors(
                params.representationToRemove.participantName
              );

              return this.caseRequestUpdateService
                .optimisticPutOrRestore(
                  params.filingId,
                  params.caseRequest,
                  caseRequestBackup
                )
                .pipe(
                  tap(() => {
                    this.validatePartiesOrchestrationService.validateParties(
                      {}
                    );
                  })
                );
            })
          );
      })
    );

  constructor(
    @Inject(FsxCaseRequestBuilderService)
    private readonly caseRequestBuilderService: ICaseRequestBuilderService,
    @Inject(FsxValidatePartiesOrchestrationService)
    private readonly validatePartiesOrchestrationService: IValidatePartiesOrchestrationService,
    @Inject(FsxCaseRequestUpdateService)
    private readonly caseRequestUpdateService: ICaseRequestUpdateService,
    @Inject(FsxValidationErrorsService)
    private readonly validationErrorsService: IValidationErrorsService
  ) {}

  /**
   * A method to allow orchestration to be triggered from components.
   *
   * @param params The parameters needed to initiate this orchestration.
   */
  removeRepresentation(params: IRemoveRepresentationEventParams): void {
    this.removeRepresentation$$.next(params);
  }
}
